import { FunctionComponent, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Div, GridBag, Page, SideNavigation, Flex, Headline, Button, Divider, Title, Loader } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash/cloneDeep';
import { useHistory } from 'react-router';
import { SideNavigationItemType } from '@gaiads/telia-react-component-library/build/types/components/SideNavigation/SideNavigationItem/SideNavigationItem';
import { ApproverList } from '@cdaas/api-clients/dist/asset-user-openapi';

import {
  getUserGroups,
  selectUserGroup,
  getUserGroupUsers,
  showGrowlNotificationTranslated,
  getAllUsers,
  saveUserGroup,
} from '../../../store/actions';
import { RootState } from '../../../store/reducers';
import OrganizationSetupHeader from '../OrganizationSetupHeader/OrganizationSetupHeader';
import ApprovalChain from '../Approval/ApprovalChain/ApprovalChain';
import {
  getAllApproversRequest,
  convertApprovalChainFromUserGroup,
  saveUserGroupApprovalChainRequest,
  convertApprovalChainToApiRequest,
} from '../../../utils';
import { ApprovalChainTemplateOption, User, UserGroup } from '../../../types';

import classes from './Approval.module.css';

const Approval: FunctionComponent = () => {
  const userGroups: UserGroup[] = useSelector((state: RootState) => state.userGroups.userGroups, shallowEqual);
  const users: User[] = useSelector((state: RootState) => state.users.users, shallowEqual);
  const isAuthenticated: boolean = useSelector((state: RootState) => state.authentication.authenticated);
  const selectedUserGroup: UserGroup | undefined = useSelector((state: RootState) => state.userGroups.selectedUserGroup, shallowEqual);

  const { t } = useTranslation('onboarding');
  const dispatch = useDispatch();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [approvalChain, setApprovalChain] = useState<ApproverList>({ contents: [] });
  const [approvalChainIsModified, setApprovalChainIsModified] = useState<boolean>(false);
  const [allApprovers, setAllApprovers] = useState<ApproverList>({ contents: [] });

  const [firstUserGroupSelectedOnInitialRender, setFirstUserGroupSelectedOnInitialRender] = useState<boolean>(false);

  const getAllApprovers = async () => {
    const approvers = await getAllApproversRequest();
    setAllApprovers(approvers);
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserGroups());
      dispatch(getAllUsers());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (userGroups.length && !firstUserGroupSelectedOnInitialRender) {
      dispatch(selectUserGroup(userGroups[0].id!));
      setFirstUserGroupSelectedOnInitialRender(true);
    }
  }, [userGroups, dispatch, firstUserGroupSelectedOnInitialRender]);

  useEffect(() => {
    if (selectedUserGroup) {
      // check if group has approval chain, if not create new default one
      setApprovalChain(convertApprovalChainFromUserGroup(selectedUserGroup.approval_chain ?? [], selectedUserGroup));
      Promise.all([
        dispatch(getUserGroupUsers(selectedUserGroup.id!)),
        // Get all approvers on every group change, if new users are added to group
        getAllApprovers(),
      ])
        .then(() => setApprovalChainIsModified(false))
        .finally(() => setIsLoading(false));
    }
    // used functions not added as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserGroup, users, userGroups]);

  const handleCancelButton = (): void => {
    if (selectedUserGroup) {
      setApprovalChain(convertApprovalChainFromUserGroup(selectedUserGroup.approval_chain ?? [], selectedUserGroup));
      setApprovalChainIsModified(false);
    }
  };

  const handleSaveButton = async (selectGroupAfterSaving: boolean): Promise<void> => {
    if (selectedUserGroup?.id) {
      const approvalChainRequest = convertApprovalChainToApiRequest(approvalChain);
      const updatedApprovalChain = await saveUserGroupApprovalChainRequest(approvalChainRequest, selectedUserGroup.id);
      const newState = cloneDeep(selectedUserGroup);
      newState!.approval_chain = updatedApprovalChain;
      dispatch(saveUserGroup(newState, selectGroupAfterSaving));
    }
  };

  const handleAddNewApproval = (newApproval: ApprovalChainTemplateOption): void => {
    const newApprover = allApprovers.contents.find(approver => approver.id === newApproval.value)!;
    setApprovalChain({ contents: [...approvalChain.contents, newApprover] });
    setApprovalChainIsModified(true);
  };

  const handleRemoveApproval = (index: number): void => {
    setApprovalChain({ contents: approvalChain.contents.filter((_x, i) => i !== index) });
    setApprovalChainIsModified(true);
  };

  const handleApprovalOrderChange = (index: number, move: 'up' | 'down'): void => {
    if (index === 1 && move === 'up') {
      return;
    } else if (index === approvalChain.contents.length - 1 && move === 'down') {
      return;
    }

    const clickedElement = { ...approvalChain.contents[index] };
    const modifiedApprovalChain = approvalChain.contents.map((el, i) => {
      if (move === 'up') {
        const elementBefore = { ...approvalChain.contents[index - 1] };
        switch (i) {
          case index - 1:
            return clickedElement;
          case index:
            return elementBefore;
          default:
            return el;
        }
      } else {
        const elementAfter = { ...approvalChain.contents[index + 1] };
        switch (i) {
          case index + 1:
            return clickedElement;
          case index:
            return elementAfter;
          default:
            return el;
        }
      }
    });
    setApprovalChainIsModified(true);
    setApprovalChain({ contents: modifiedApprovalChain });
  };

  const handleGroupSelection = (id: string): void => {
    setIsLoading(true);
    dispatch(selectUserGroup(id));
  };

  const noChangesInForm = !approvalChainIsModified;

  const navigationItems: SideNavigationItemType[] = userGroups.map<SideNavigationItemType>(ug => ({
    label: ug.name,
    active: ug.id === selectedUserGroup?.id,
    onClick: async () => {
      if (!noChangesInForm) {
        await handleSaveButton(false);
      }
      if (ug.id !== selectedUserGroup?.id) {
        handleGroupSelection(ug.id!);
      }
    },
    key: ug.id,
  }));

  const finishOnboarding = () => {
    if (!noChangesInForm) {
      return dispatch(showGrowlNotificationTranslated('onboarding:groupSettingsPage.notReadyToFinish', 'warning'));
    }
    history.push('/');
  };

  return (
    <Page>
      <OrganizationSetupHeader onboardingCurrentStepIndex={3} inputData={userGroups} validateAndSaveOnboardingStepData={finishOnboarding} />
      <Flex>
        <SideNavigation className={classes.sidenavigation} navigationItems={navigationItems} padding={{ top: 'xlg' }} />
        {!!selectedUserGroup && (
          <Div occupyHorizontalSpace>
            <Flex occupyVerticalSpace={false} centeredVertically spaceBetween margin={{ left: 'xlg', vertical: 'md' }}>
              <Headline size={'md'}>{selectedUserGroup.name}</Headline>
              <Div>
                {approvalChainIsModified && (
                  <Button onClick={handleCancelButton} margin={{ right: 'md' }} variant="text" size="lg">
                    {t('groupSettingsPage.cancelButton')}
                  </Button>
                )}
                <Button onClick={() => handleSaveButton(true)} disabled={noChangesInForm}>
                  {t('groupSettingsPage.saveButton')}
                </Button>
              </Div>
            </Flex>
            <Div margin={{ left: 'xlg' }}>
              <Divider />
            </Div>
            <Div margin={{ size: 'xlg' }}>
              <GridBag>
                <GridBag.Item md={12}>
                  <Title margin={{ size: 'sm' }} size={'sm'}>
                    {t('groupSettingsPage.approvalChainTitle')}
                  </Title>
                  <Div
                    className={classes['form-width']}
                    boxShadow={'sm'}
                    borderRadius={{ size: 'md' }}
                    padding={{ size: 'md' }}
                    backgroundColor="white"
                  >
                    {isLoading ? (
                      <Flex margin={{ top: 'lg' }} centered occupyHorizontalSpace>
                        <Loader />
                      </Flex>
                    ) : (
                      <ApprovalChain
                        selectedUserGroup={selectedUserGroup}
                        approvalChain={approvalChain}
                        allApprovers={allApprovers}
                        handleAddNewApproval={handleAddNewApproval}
                        handleRemoveApproval={handleRemoveApproval}
                        handleApprovalOrderChange={handleApprovalOrderChange}
                      />
                    )}
                  </Div>
                </GridBag.Item>
              </GridBag>
            </Div>
          </Div>
        )}
      </Flex>
    </Page>
  );
};

export default Approval;
